/**
 * Monthly calendar screen (Task 13 / docs/04 §6).
 * Two economics blocks — never one combined “net after fees and interest” figure.
 */

import { formatCentsDisplay } from '../../domain/recommend/explain';
import type { Cents } from '../../domain/money';
import type { MonthlyCalendar } from '../../domain/monthlyCalendar';

export type MonthlyCalendarScreenProps = {
  calendar: MonthlyCalendar;
};

function formatMoney(cents: Cents): string {
  return formatCentsDisplay(cents);
}

function FlagChip({ label, active }: { label: string; active: boolean }): JSX.Element | null {
  if (!active) return null;
  return (
    <span
      data-testid="calendar-flag"
      data-flag={label}
      className="rounded border border-rose-800 px-2 py-0.5 text-xs text-rose-300"
    >
      {label}
    </span>
  );
}

export function MonthlyCalendarScreen({ calendar }: MonthlyCalendarScreenProps): JSX.Element {
  const econ = calendar.portfolio_economics;
  const rec = calendar.recommender_value;

  return (
    <section data-testid="monthly-calendar-screen" className="flex flex-col gap-4">
      <div>
        <h2 className="text-lg font-semibold text-slate-100">Monthly calendar</h2>
        <p className="mt-1 text-sm text-slate-400">
          {calendar.month_start} → {calendar.month_end}. Contractual obligation is the statement
          balance by the due date; weekly cash management is separate.
        </p>
      </div>

      {calendar.cards.map((card) => (
        <article
          key={card.card_id}
          data-testid="monthly-calendar-card"
          data-card-id={card.card_id}
          className="rounded-lg border border-slate-800 bg-slate-900/60 p-4"
        >
          <header className="mb-2 flex flex-wrap items-start justify-between gap-2">
            <div>
              <h3 className="text-base font-medium text-slate-100">{card.label}</h3>
              <p className="text-xs text-slate-500">
                Grace: {card.grace_status} — {card.grace_reason}
              </p>
            </div>
            <div className="flex flex-wrap gap-1">
              <FlagChip label="payment shortfall" active={card.flags.payment_shortfall} />
              <FlagChip label="grace attention" active={card.flags.grace_attention} />
              <FlagChip label="uncovered due" active={card.flags.uncovered_due} />
              <FlagChip label="projected dates" active={card.flags.has_projected_dates} />
            </div>
          </header>

          <ul className="flex flex-col gap-1 border-t border-slate-800 pt-2">
            {card.timeline.length === 0 ? (
              <li className="text-sm text-slate-500">No events this month.</li>
            ) : (
              card.timeline.map((ev, i) => (
                <li
                  key={`${ev.kind}-${ev.date}-${String(i)}`}
                  data-testid="calendar-event"
                  data-event-kind={ev.kind}
                  data-projected={ev.is_projected ? 'true' : 'false'}
                  className={
                    ev.is_projected ? 'text-sm italic text-slate-400' : 'text-sm text-slate-200'
                  }
                >
                  {ev.date} · {ev.label}
                  {ev.amount_cents !== null ? ` · ${formatMoney(ev.amount_cents)}` : ''}
                  {ev.remaining_due_cents !== null
                    ? ` · remaining ${formatMoney(ev.remaining_due_cents)}`
                    : ''}
                  {ev.is_projected ? ' · projected' : ''}
                </li>
              ))
            )}
          </ul>
        </article>
      ))}

      <div
        data-testid="portfolio-economics-block"
        className="rounded-lg border border-slate-800 bg-slate-900/40 p-4"
      >
        <h3 className="text-sm font-medium text-slate-200">Portfolio card economics</h3>
        <p className="mt-1 text-xs text-slate-500">
          Honest portfolio picture — not a measure of the recommender.
        </p>
        <dl className="mt-3 flex flex-col gap-1 text-sm text-slate-200">
          <div className="flex justify-between gap-3">
            <dt>Purchases</dt>
            <dd>{formatMoney(econ.purchases_cents)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt>Payments</dt>
            <dd>{formatMoney(econ.payments_cents)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt>Remaining obligations</dt>
            <dd>{formatMoney(econ.remaining_obligations_cents)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt>Next-cycle purchases</dt>
            <dd>{formatMoney(econ.next_cycle_purchases_cents)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt>Rewards earned ({econ.rewards_earned_label})</dt>
            <dd>+{formatMoney(econ.rewards_earned_cents)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt>Expected rewards (unreconciled)</dt>
            <dd>+{formatMoney(econ.expected_rewards_unreconciled_cents)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt>Offers / statement credits</dt>
            <dd>+{formatMoney(econ.offers_statement_credits_cents)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt>Card fees</dt>
            <dd>−{formatMoney(econ.card_fees_cents)}</dd>
          </div>
          <div className="flex justify-between gap-3">
            <dt>Interest charged</dt>
            <dd>−{formatMoney(econ.interest_charged_cents)}</dd>
          </div>
          <div className="mt-1 flex justify-between gap-3 border-t border-slate-700 pt-2 font-medium">
            <dt>Net portfolio card economics</dt>
            <dd>{formatMoney(econ.net_portfolio_card_economics_cents)}</dd>
          </div>
        </dl>
      </div>

      <div
        data-testid="recommender-value-block"
        className="rounded-lg border border-slate-800 bg-slate-900/40 p-4"
      >
        <h3 className="text-sm font-medium text-slate-200">
          Value generated by purchases recommended through CardZapp
        </h3>
        <p className="mt-1 text-xs text-slate-500">
          Separate from portfolio economics — the only fair measure of the recommender (
          {rec.value_generated_label}).
        </p>
        <p className="mt-2 text-base text-slate-100">+{formatMoney(rec.value_generated_cents)}</p>
      </div>
    </section>
  );
}
